import { ICONS, type IconName } from './icons'

export type { IconName }

export interface IconProps {
  /** Key into `ICONS` — one of the 46 vendored Lucide icons. */
  name: IconName
  /** Pixel size for both width and height. The design default is 17. */
  size?: number
  /**
   * Accessible label. Pass this only when the icon is the only content of a
   * control (no adjacent text label) — everywhere else in this design an
   * icon sits beside its own label, so the icon itself stays decorative.
   */
  label?: string
  className?: string
}

const DEFAULT_SIZE = 17

/**
 * Renders one of the vendored Lucide icons as an inline SVG. `stroke` is
 * always `currentColor` so the icon follows whatever text-colour class
 * (`text-mid`, `text-bad`, a channel colour, ...) the caller applies — one
 * markup, every theme, every tone. `strokeWidth`/`strokeLinecap`/
 * `strokeLinejoin` are fixed at Lucide's vendored 2px-round-round: every one
 * of the 46 source files carries those three on its own outer `<svg>`, and
 * `ICONS` only keeps the inner markup (see icons.ts), so the wrapper has to
 * restore them itself or every icon silently falls back to the browser's
 * 1px/butt/miter defaults.
 */
export function Icon({ name, size = DEFAULT_SIZE, label, className }: IconProps) {
  const hidden = label === undefined

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={hidden ? 'true' : undefined}
      role={hidden ? undefined : 'img'}
    >
      {/*
       * `label` is free-form text that can come from untrusted data further
       * up the call chain (a caller-ID name, an SMS sender). It is rendered
       * as a real JSX child — not concatenated into `dangerouslySetInnerHTML`
       * — so React escapes it exactly like any other text node. Only the
       * vendored, closed-union `ICONS[name]` markup goes through
       * `dangerouslySetInnerHTML`, and it is scoped to this inner `<g>` so a
       * label can never be parsed as part of that HTML string.
       */}
      {!hidden && <title>{label}</title>}
      <g dangerouslySetInnerHTML={{ __html: ICONS[name] }} />
    </svg>
  )
}
